import EmptyState from '@local/web-design-system-2/dist/components/EmptyState/EmptyState';
import Box from '@mui/material/Box';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { Addon } from 'src/apiClients/queries';
import { DataTable } from 'src/components/data-table/DataTable';
import {
    SortDirection,
    type DataTableCellValue,
    type DataTableColDef,
    type DataTableRow,
} from 'src/components/data-table/DataTable.types';
import { i18n as routesI18n } from 'src/hooks/use-imago-mp-routes.i18n';
import { createNaFallback, createStateLabel } from 'src/utils/table-formatters';

import { useInstance } from '../../InstanceContext';
import { SectionHeading } from '../SectionHeading';
import { i18n } from './SubscriptionsSection.i18n';

const PRODUCT_CODE_NAMES: Record<string, string> = {
    'IMAGO-S': 'Imago Standard',
    'IMAGO-E': 'Imago Enterprise',
    'IMAGO-J': 'Imago Junior',
    'IMAGO-CAPACITY-UNITS': 'Seequent Monthly Capacity Units',
    'IMAGO-S-H': 'Imago Standard Hibernation',
    'IMAGO-E-H': 'Imago Enterprise Hibernation',
    'IMAGO-J-H': 'Imago Junior Hibernation',
    'EXTRA-STORAGE': 'Imago Extra Storage - 1 TB/Workspace - Subscription',
    'MOBILE-CAPTURE': 'Imago Mobile Capture - Subscription',
    'IMAGO-EAP': 'Imago Early Access Programme',
    'IMAGO-DATA-SHARING': 'Imago Data Sharing',
    'CAPTURE-CHIP': 'Imago Capture - Chip Shot - Subscription',
    'CAPTURE-CORE': 'Imago Capture - Core Shot - Subscription',
};

const isActiveByDate = (finishDate: string): boolean =>
    !!finishDate && new Date(finishDate) > new Date();

const mapRow = (addon: Addon): DataTableRow => ({
    id: addon.id,
    name: PRODUCT_CODE_NAMES[addon.name] ?? addon.name,
    productCode: addon.name,
    quoteId: addon.salesforceQuoteNumber,
    serialNumber: addon.id,
    type: addon.type,
    productLevel: addon.productLevel,
    quantity: addon.quantity,
    startDate: addon.startDate,
    finishDate: addon.finishDate,
    state: isActiveByDate(addon.finishDate) ? 'A' : 'I',
});

export const SubscriptionsSection = () => {
    const intl = useIntl();
    const { subscription } = useInstance();

    const rows = useMemo<DataTableRow[]>(
        () => subscription.addons.map(mapRow),
        [subscription.addons],
    );

    const columns = useMemo<DataTableColDef[]>(() => {
        const naFallback = createNaFallback(intl);
        const stateLabel = createStateLabel(intl);
        const dateFormatter = (value: DataTableCellValue): string => {
            if (value === null || value === undefined || value === '') {
                return naFallback(value);
            }
            const date = new Date(String(value));
            if (isNaN(date.getTime())) return naFallback(null);
            return intl.formatDate(date, { year: 'numeric', month: 'short', day: 'numeric' });
        };
        return [
            {
                field: 'name',
                headerName: intl.formatMessage(i18n.name),
                flex: 2,
                minWidth: 160,
                valueFormatter: naFallback,
            },
            {
                field: 'productCode',
                headerName: intl.formatMessage(i18n.productCode),
                width: 200,
                valueFormatter: naFallback,
            },
            {
                field: 'type',
                headerName: intl.formatMessage(i18n.type),
                width: 120,
                valueFormatter: naFallback,
            },
            {
                field: 'productLevel',
                headerName: intl.formatMessage(i18n.productLevel),
                width: 130,
                valueFormatter: naFallback,
            },
            {
                field: 'quoteId',
                headerName: intl.formatMessage(i18n.quoteId),
                width: 150,
                valueFormatter: naFallback,
            },
            {
                field: 'quantity',
                headerName: intl.formatMessage(i18n.quantity),
                width: 100,
                valueFormatter: (value) =>
                    value === null || value === undefined ? naFallback(value) : String(value),
            },
            {
                field: 'startDate',
                headerName: intl.formatMessage(i18n.startDate),
                width: 140,
                valueFormatter: dateFormatter,
            },
            {
                field: 'finishDate',
                headerName: intl.formatMessage(i18n.finishDate),
                width: 140,
                valueFormatter: dateFormatter,
            },
            {
                field: 'serialNumber',
                headerName: intl.formatMessage(i18n.serialNumber),
                flex: 2,
                minWidth: 200,
            },
            {
                field: 'state',
                headerName: intl.formatMessage(i18n.status),
                width: 110,
                filterOptions: [
                    { label: stateLabel('A'), value: 'A' },
                    { label: stateLabel('I'), value: 'I' },
                ],
                valueFormatter: stateLabel,
            },
        ];
    }, [intl]);

    const heading = (
        <SectionHeading
            title={intl.formatMessage(routesI18n.instanceSectionSubscriptions)}
            count={rows.length}
            automationId="instance-subscriptions-count"
        />
    );

    if (rows.length === 0) {
        return (
            <>
                {heading}
                <Box sx={{ py: 8 }} automation-id="instance-subscriptions-empty">
                    <EmptyState
                        title={intl.formatMessage(i18n.noSubscriptionsTitle)}
                        message={intl.formatMessage(i18n.noSubscriptionsMessage)}
                    />
                </Box>
            </>
        );
    }

    return (
        <>
            {heading}
            <DataTable
                columns={columns}
                rows={rows}
                totalRows={rows.length}
                initialSort={[{ field: 'name', direction: SortDirection.Asc }]}
                disableScrollbarGutter
                titleWhenNoResults={intl.formatMessage(i18n.noSubscriptionsTitle)}
                messageWhenNoResults={intl.formatMessage(i18n.noSubscriptionsMessage)}
            />
        </>
    );
};
